import { Button, Typography, Box, Paper, TextField } from "@mui/material";
import { styled } from "@mui/system";
import { InputAdornment } from "@mui/material";
import { Controller } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { formatInternationalDigits } from "../../utils/phone";

const VerifyMobileNumberContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  paddingTop: "0.625rem",
  fontWeight: 500,
  color: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  maxWidth: "100%",
  maxHeight: "100vh",
});

const Logo = styled("img")({
  alignSelf: "center",
  aspectRatio: "1.92",
  width: "94px",
});

const ContentContainer = styled(Paper)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  paddingTop: "5rem",
  paddingBottom: "1.75rem",
  marginTop: "0.75rem",
  width: "100%",
  backgroundColor: "#756DE9",
  "@media (max-width: 600px)": {
    borderRadius: "0.25rem",
  },
  clipPath: "circle(97.1% at 73% 82%)",
});

const Title = styled(Typography)({
  marginTop: "1rem",
  fontSize: "1.5rem",
  lineHeight: "2.25rem",
  textAlign: "center",
  color: "#ffffff",
});

const Subtitle = styled(Typography)({
  marginTop: "1.75rem",
  fontSize: "0.875rem",
  lineHeight: "1.5rem",
  textAlign: "center",
  color: "#ffffff",
});

const OTPContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignSelf: "stretch",
  marginTop: "1.5rem",
  fontSize: "1.125rem",
  whiteSpace: "nowrap",
});

const CopyIcon = styled("img")({
  flexShrink: 0,
  aspectRatio: "1 / 1",
  width: "21px",
});

const ResendCodeContainer = styled(Box)({
  display: "flex",
  gap: "0.625rem",
  justifyContent: "center",
  padding: "0.625rem",
  marginTop: "10rem",
  fontSize: "1rem",
  fontWeight: 600,
});

const RefreshIcon = styled("img")({
  flexShrink: 0,
  width: "1.5rem",
  aspectRatio: "1 / 1",
});

const WelcomeButton = styled(Button)({
  display: "flex",
  justifyContent: "center",
  padding: "15px 64px",
  color: "#3F51B5",
  backgroundColor: "#FFFFFF",
  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.2)",
  borderRadius: "95px",
  marginTop: "16px",
  fontWeight: "600",
  lineHeight: "36px",
  fontSize: "1rem",
  textTransform: "none",
  "&:hover": {
    bgcolor: "rgba(255, 255, 255, 0.9)",
  },
});

const VerifyMobileNumber = ({
  mobileNumber,
  resendCodeDuration,
  control,
  handleSubmit,
  onSubmit,
  otp,
  handleSendOtp,
}: any) => {
  const ajaxCallsInProgress = useSelector((state: any) => state.ajaxCallsInProgress);
  const [countdown, setCountdown] = useState(resendCodeDuration);
  const mobileDisplay = useMemo(() => formatInternationalDigits(mobileNumber), [mobileNumber]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prevCountdown: number) => prevCountdown - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [countdown]);

  const handleResendClick = () => {
    // Reset coun:tdown timer and trigger resend functionality
    setCountdown(resendCodeDuration);
    handleSendOtp({ phoneNumber: mobileNumber });
    // Add logic to resend OTP
  };

  return (
    <VerifyMobileNumberContainer>
      <Logo
        src="https://cdn.builder.io/api/v1/image/assets/TEMP/2e88e2fbec7ace948fcde95ec056e8b760fc06d3dfba3d5d3f7fa80729a3c10b?apiKey=80f439f9d23243918de9eba558cc207a&"
        alt="Logo"
        loading="lazy"
      />
      <ContentContainer elevation={0}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center", // Centers children
            width: "100%",
          }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Title variant="h1">Verify Mobile Number</Title>
          <Subtitle>OTP has been sent to {mobileDisplay}</Subtitle>
          <OTPContainer>
            <Controller
              control={control}
              name="otp"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  sx={{
                    display: "flex",
                    gap: "1.25rem",
                    justifyContent: "space-between",
                    padding: "0 0.5rem",
                    backgroundColor: "rgba(63, 81, 181, 0.3)",
                    border: "none",
                    borderRadius: "100px",
                    boxShadow: "inset 0px 0px 10px rgba(0, 0, 0, 0.2)",
                    margin: "0 40px",
                  }}
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                  onChange={(e) => {
                    const regex = /^[0-9\b]+$/;
                    if (e.target.value === "" || regex.test(e.target.value)) {
                      field.onChange(e.target.value.slice(0, 10));
                    }
                  }}
                  InputProps={{
                    style: { color: "#ffffff" }, // Style the input text color
                    endAdornment:
                      otp?.length === 4 ? (
                        <InputAdornment position="end">
                          <CopyIcon
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/657e2705678a2ab25fe4ae25a2976df3c35793d8d4907f44d5c1d9c349dc43ae?apiKey=80f439f9d23243918de9eba558cc207a&"
                            alt="Copy Icon"
                            loading="lazy"
                          />
                        </InputAdornment>
                      ) : (
                        <></>
                      ),
                  }}
                />
              )}
            />
          </OTPContainer>
          <ResendCodeContainer>
            <RefreshIcon
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/e6e0ab4ce6d77d22dd7d8fd969f870aba1c2abe9d0f9fd68724f55a84938e7d7?apiKey=80f439f9d23243918de9eba558cc207a&"
              alt="Refresh Icon"
              loading="lazy"
            />

            {countdown === 0 ? (
              <Typography sx={{ color: "#fff" }} onClick={handleResendClick}>
                Resend Code
              </Typography>
            ) : (
              <Typography sx={{ color: "#fff" }}>Resend Code in {countdown}</Typography>
            )}
          </ResendCodeContainer>

          <WelcomeButton type="submit" variant="contained" disabled={!otp || !!ajaxCallsInProgress}>
            Welcome to SeeVi
          </WelcomeButton>
        </Box>
      </ContentContainer>
    </VerifyMobileNumberContainer>
  );
};

export default VerifyMobileNumber;
